// Copyright 2015-2018 The MathWorks, Inc.

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "MW/uiframework/contextmanager/FeatureManager"
], function (declare, lang, FeatureManager) {
    return declare([FeatureManager], {

        /**
         * @param args {{contextManagerImpl: ContextManager, uiContainer: UIContainer}}
         */
        constructor: function (args) {
            this._uiContainer = args.uiContainer;
            this._registeredPanels = {};
            this._currentContextualPanelIds = [];
            this._stateForContext = {};
        },

        /**
         * @param args {appId: string, contextId: string, panelIds: [string]}
         */
        registerPanels: function (args) {
            if (!this._registeredPanels[args.appId]) {
                this._registeredPanels[args.appId] = {};
            }

            if (!this._registeredPanels[args.appId][args.contextId]) {
                this._registeredPanels[args.appId][args.contextId] = {};
            }

            if (args.panelIds) {
                this._registeredPanels[args.appId][args.contextId].panels = args.panelIds;
            }
        },

        /**
         * @param args {appId: string, contextId: string}
         */
        unregisterPanels: function (args) {
            if (this._registeredPanels[args.appId]) {
                if (this._registeredPanels[args.appId][args.contextId]) {
                    delete this._registeredPanels[args.appId][args.contextId];
                } else {
                    delete this._registeredPanels[args.appId];
                }
            }
        },

        getContextualStates: function () {
            // Refresh the current context's state
            var currentContext = this._contextManagerImpl.get("currentContext");
            if (currentContext && !this._isAppContext(currentContext)) {
                this._stateForContext[this._generateContextKey(currentContext)] = this._uiContainer._getContextualState(currentContext.appId);
            }
            return this._stateForContext;
        },

        setContextualStates: function (states) {
            lang.mixin(this._stateForContext, states);
        },

        _update: function (context, secondaryContext) {
            if (!this._uiContainer) {
                return;
            }
            var that = this;

            // Save UIContainer state associated with the previous context
            var occupiedBorders = this._uiContainer.getOccupiedBorders();
            if (this._lastContext && !this._isAppContext(this._lastContext)) {
                this._stateForContext[this._generateContextKey(this._lastContext)] = this._uiContainer._getContextualState(this._lastContext.appId);
            }

            // Get the list of panel Ids associated with the new contexts
            var newContextualPanelIds = this._getPanelIdsForContext(context);
            var secondaryPanelIds = this._getPanelIdsForContext(secondaryContext);
            newContextualPanelIds = newContextualPanelIds.concat(secondaryPanelIds);

            // Remove any panels associated with the old context that are not relevant to the new context
            // If the context "appId" has changed then all panels must be removed, even those relevant to the new context,
            // because panels that are staying may still need to be placed in a new AccordionContainer
            var panelsChanged;
            var appChanged = this._lastContext && context && this._lastContext.appId !== context.appId;
            this._currentContextualPanelIds.forEach(function (panelId) {
                if (appChanged || newContextualPanelIds.indexOf(panelId) < 0) {
                    that._uiContainer._removeFromLayout(panelId);
                    panelsChanged = true;
                }
            });

            // Add panels associated with the new context to the UIContainer
            newContextualPanelIds.forEach(function (panelId) {
                if (appChanged || that._currentContextualPanelIds.indexOf(panelId) < 0) {
                    var panelSetId = secondaryPanelIds.indexOf(panelId) >= 0 ? undefined : context.appId;
                    that._uiContainer._addToLayout(panelId, panelSetId);
                    panelsChanged = true;
                }
            });

            // Retrieve the UIContainer state from the last time this context was active and put the UIContainer
            // back in this state.
            // Due to the current implementation of ArrangeableBorderContainer.setState the setContextualState call must be made after
            // the new panels have been added above.
            if (panelsChanged && this._lastContext && JSON.stringify(this._lastContext) !== JSON.stringify(context)) {
                // Get the contextual state to restore.  Prune collapsed states associated with existing borders.
                var prevState = this._stateForContext[this._generateContextKey(context)];
                if (prevState && occupiedBorders.length) {
                    prevState = lang.clone(prevState);
                    occupiedBorders.forEach(function (region) {
                        if (prevState[region] && prevState[region].border) {
                            delete prevState[region].border;
                        }
                    });
                }
                if (prevState) {
                    this._uiContainer._setContextualState(prevState, context.appId);
                }
            }

            this._lastContext = context;
            this._currentContextualPanelIds = newContextualPanelIds;
        },

        _isAppContext: function (context) {
            return !context || context.appId === this._contextManagerImpl.parentAppId;
        },

        _getPanelIdsForContext: function (context) {
            var panelIds = [];
            if (context && context.contextIdList && this._registeredPanels[context.appId]) {
                context.contextIdList.forEach(function (contextId) {
                    if (this._registeredPanels[context.appId][contextId]) {
                        var contextPanelIds = this._registeredPanels[context.appId][contextId].panels;
                        if (contextPanelIds) {
                            panelIds = panelIds.concat(contextPanelIds);
                        }
                    }
                }, this);
            }
            return panelIds;
        },

        /*
         * Generates a key used to store context related state
         */
        _generateContextKey: function (context) {
            var key = "null";
            if (context) {
                key = context.appId;
                context.contextIdList.forEach(function (contextId) {
                    if (contextId !== "global") {
                        key += "_" + contextId;
                    }
                });
            }
            return key;
        }
    });
});